import * as React from 'react';
import { useRef, useCallback } from 'react';
import { radius, colors } from '../../style';
import useWindowSize from '../utils/windowSize';

interface FunctionBoxProps {
  /** Optional style overrides */
  style?: React.CSSProperties
  /** Disables the functionality and makes it content slightly faded */
  disabled?: boolean
  /** Time limit for trigger the hold event */
  holdLimit?: number
  /** Event to trigger when the component is pressed */
  onClick?: () => void
  /** Event to trigger when the component is well hold */
  onHold?: () => Promise<void>

  elref?: React.RefObject<HTMLDivElement>
  children?: React.ReactNode
}

function usePressAndHold(props: FunctionBoxProps) {
  const buttonPressTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const holding = useRef(false);

  const onLongPress = useCallback(async () => {
    if (props.onHold) {
      holding.current = true;
      await props.onHold();
    }
    holding.current = false;
  }, [props.onHold]);

  const handlePress = useCallback(() => {
    buttonPressTimer.current = setTimeout(() => {
      onLongPress();
    }, props.holdLimit || 1000);
  }, [props.holdLimit, onLongPress]);

  const handleRelease = useCallback(() => {
    holding.current = false;
    buttonPressTimer.current && clearTimeout(buttonPressTimer.current);
  }, []);

  const onShortPress = useCallback(() => {
    if (props.onClick && !holding.current) {
      props.onClick();
    }
  }, [props.onClick]);

  return { handlePress, handleRelease, onShortPress };
}

/** A standardized box with on click and press and hold functionality */
export function FunctionBox(props: FunctionBoxProps) {
  const { disabled, style, children, elref } = props;
  const { handlePress, handleRelease, onShortPress } = usePressAndHold(props);

  return (
    <div
      ref={elref}
      onTouchStart={!disabled ? handlePress : undefined}
      onTouchEnd={!disabled ? handleRelease : undefined}
      onMouseDown={!disabled ? handlePress : undefined}
      onMouseUp={!disabled ? handleRelease : undefined}
      onMouseLeave={!disabled ? handleRelease : undefined}
      onClick={!disabled ? onShortPress : undefined}
      style={{
        opacity: disabled ? 0.6 : 1,
        scrollSnapAlign: 'start',
        minHeight: 120,
        minWidth: 120,
        borderRadius: radius.large,
        display: 'flex',
        whiteSpace: 'nowrap',
        flexDirection: 'column',
        border: "1px solid",
        borderColor: colors.faded,
        boxSizing: 'border-box',
        marginRight: 20,
        justifyContent: 'center',
        alignItems: 'center',
        ... (style || {}),
      }}
    >
      {children}
    </div>
  );
}

/** A function box with on click and press and hold functionality formed as a pill */
export function FunctionPill(props: FunctionBoxProps) {
  const { disabled, style, children } = props;
  const { handlePress, handleRelease, onShortPress } = usePressAndHold(props);

  return (
    <div
      onTouchStart={!disabled ? handlePress : undefined}
      onTouchEnd={!disabled ? handleRelease : undefined}
      onMouseDown={!disabled ? handlePress : undefined}
      onMouseUp={!disabled ? handleRelease : undefined}
      onMouseLeave={!disabled ? handleRelease : undefined}
      onClick={!disabled ? onShortPress : undefined}
      style={{
        opacity: disabled ? 0.6 : 1,
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        border: '1px solid',
        borderColor: colors.faded,
        borderRadius: radius.large,
        boxSizing: 'border-box',
        padding: 7,
        marginBottom: 7,
        ... (style || {}),
        cursor: "pointer"
      }}
    >
      {children}
    </div>
  );
}

/** A function box with on click and press and hold functionality formed as a grid */
export function FunctionGrid(props: FunctionBoxProps) {
  const { disabled, style, children } = props;
  const { handlePress, handleRelease, onShortPress } = usePressAndHold(props);
  const { isDesktop } = useWindowSize();

  return (
    <div
      onTouchStart={!disabled ? handlePress : undefined}
      onTouchEnd={!disabled ? handleRelease : undefined}
      onMouseDown={!disabled ? handlePress : undefined}
      onMouseUp={!disabled ? handleRelease : undefined}
      onMouseLeave={!disabled ? handleRelease : undefined}
      onClick={!disabled ? onShortPress : undefined}
      style={{
        opacity: disabled ? 0.6 : 1,
        scrollSnapAlign: 'start',
        minHeight: isDesktop ? '142px' : '38vw',
        minWidth: isDesktop ? '142px' : '38vw',
        borderRadius: radius.large,
        display: 'flex',
        whiteSpace: 'nowrap',
        flexDirection: 'column',
        border: "1px solid",
        borderColor: colors.faded,
        boxSizing: 'border-box',
        justifyContent: 'center',
        alignItems: 'center',
        height: isDesktop ? '150px' : 'none',
        width: isDesktop ? '150px' : 'none',
        ... (style || {})
      }}
    >
      {children}
    </div>
  );
}
